import { createRouter, createWebHistory } from "vue-router";
import { useUsuariosStore, authReadyPromise } from "@/stores/usuarios";

// La triada del trabajo diario (Agenda/Pacientes/PacienteDetalle) va eager: es el primer
// paint tras entrar y se navega entre ellas todo el tiempo.
import AgendaView from "@/views/AgendaView.vue";
import PacientesView from "@/views/PacientesView.vue";
import PacienteDetalleView from "@/views/PacienteDetalleView.vue";

// El resto va lazy (chunk propio, se baja al entrar a la ruta):
//  - Admin/Reportes: solo las abre el dueño (rol admin); una doctora nunca las carga.
//  - Login: se usa una vez al entrar; Landing: la doctora logueada va directo a /agenda.

const routes = [
  { path: "/", component: () => import(/* webpackChunkName: "landing" */ "@/views/LandingView.vue"), meta: { requiresAuth: false } },
  {
    path: "/login",
    component: () => import(/* webpackChunkName: "login" */ "@/views/LoginView.vue"),
    meta: { requiresAuth: false }
  },
  {
    path: "/admin",
    component: () => import(/* webpackChunkName: "admin" */ "@/views/AdminView.vue"),
    meta: { requiresAuth: true, requiresRole: 'admin' }
  },
  {
    path: "/reportes",
    component: () => import(/* webpackChunkName: "reportes" */ "@/views/ReportesView.vue"),
    meta: { requiresAuth: true, requiresRole: 'admin' }
  },
  { 
    path: "/agenda",
    component: AgendaView,
    meta: { requiresAuth: true, allowedRoles: ['admin', 'doctor'] }
  },
  { 
    path: "/pacientes",
    component: PacientesView,
    meta: { requiresAuth: true, allowedRoles: ['admin', 'doctor'] }
  },
  { 
    path: "/paciente/:id", 
    component: PacienteDetalleView,
    meta: { requiresAuth: true, allowedRoles: ['admin', 'doctor'] }
  },
  {
    path: '/soporte',
    name: 'Soporte',
    component: () => import('@/views/SoporteView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/privacidad',
    component: () => import('@/views/PrivacidadView.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/terminos',
    component: () => import('@/views/TerminosView.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/',
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  // eslint-disable-next-line no-unused-vars
  scrollBehavior(to, from, savedPosition) {
    // Siempre volver al top cuando se cambia de ruta
    return { top: 0, behavior: 'instant' }
  }
});

// Middleware de autenticación y roles
router.beforeEach(async (to, from, next) => {
  await authReadyPromise;
  const usuariosStore = useUsuariosStore();
  const requiresAuth = to.meta.requiresAuth;
  const requiresRole = to.meta.requiresRole;
  const allowedRoles = to.meta.allowedRoles;

  // Si la ruta no requiere autenticación, permitir acceso
  if (requiresAuth === false) {
    return next();
  }

  // Verificar si está autenticado
  if (requiresAuth && !usuariosStore.usuarioActual) {
    return next('/login');
  }

  // Verificar autorización
  if (requiresAuth && !usuariosStore.usuarioAutorizado) {
    return next('/login');
  }

  // Verificar rol específico
  if (requiresRole && usuariosStore.usuarioActual?.rol !== requiresRole) {
    console.warn(`Acceso denegado: se requiere rol ${requiresRole}`);
    return next('/agenda'); // Redirigir a agenda si no tiene el rol
  }

  // Verificar roles permitidos
  if (allowedRoles && !allowedRoles.includes(usuariosStore.usuarioActual?.rol)) {
    console.warn(`Acceso denegado: rol no permitido`);
    return next('/agenda'); // Redirigir a agenda si no tiene el rol
  }

  // Todo OK, permitir acceso
  next();
});

// SEO: canonical y robots dinámico por ruta
router.afterEach((to) => {
  const esPublica = to.meta.requiresAuth === false && to.path !== '/login';

  // Canonical dinámico
  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  const canonicalPath = esPublica ? to.path : '/';
  canonical.setAttribute('href', `https://vedent.app${canonicalPath}`);

  // Meta robots dinámico
  let robots = document.querySelector('meta[name="robots"]');
  if (!robots) {
    robots = document.createElement('meta');
    robots.setAttribute('name', 'robots');
    document.head.appendChild(robots);
  }
  robots.setAttribute('content', esPublica ? 'index, follow' : 'noindex, nofollow');
});

export default router;
